import createEmojiPlugin from '@draft-js-plugins/emoji';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { collection, doc, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { db, storage } from 'utils/firebase';
// mui components
import {
  Avatar,
  Button,
  Collapse,
  Grid,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
// components
import DefaultAvatar from 'components/default-avatar';
import UploadedFiles from 'components/uploaded-files';
import UploadedImages from 'components/uploaded-images';
import Player from 'components/player';
import TextEditor from '../text-editor';
// custom hooks
import { StatusValue } from 'components/activity-feed/components/upload-file/components/Dropzone';
import { TranscodingJob, useFileUpload } from 'hooks/useFileUpload';
import { StorageLimitExceededError } from 'hooks/useStorageUploadGuard';
import { useVideoThumbnails } from 'hooks/useVideoThumbnails';
import { useDeleteUnsupportedEmojies } from './hooks/useDeleteUnsupportedEmojies';
import { useDrafts } from './hooks/useDrafts/useDrafts';
import { getDraftType, getMediaType } from './hooks/useDrafts/utils';
// types
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import {
  ActivityFeedStatusEnum,
  IActivityFeedProfile,
  IUpdatePostBody,
  PostVisibilityEnum,
} from 'redux-store/api/activity-feed';
import { IStyles, ITag } from 'redux-store/types';
import { IUploadingFiles } from 'utils/types';
// helpers
import {
  convertHtml,
  createDraftContent,
  createMentionToContentHelper,
  createMentionsArray,
  getFilenameFromUrl,
  getHashtagsArray,
  getImageBySize,
  isImageFilename,
} from 'utils/helpers';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import {
  createFileLink,
  createMediaEntry,
  deleteMediaEntry,
} from 'utils/services/fileCabinetService';
import type { FileLinkContextType } from 'redux-store/api/file-cabinet';
import { isAudio, isVideo as isVideoFile } from 'components/uploaded-images/helpers';
import { activityFeedSchema, postReplySchema } from './validation';
// icons
import AttachFileIcon from 'assets/icons/buttons/attach-file-icon.svg';
import AttachImageIcon from 'assets/icons/buttons/attach-image-icon.svg';
import CabinetFileIcon from 'assets/icons/new/selection/file-menu-cabinet.svg';
import ChevronIcon from 'assets/icons/buttons/chevron-down.svg';
import EmojiIcon from 'assets/icons/buttons/emoji-icon.svg';
import CancelIcon from 'assets/icons/buttons/cancel-request.svg';
import UploadFileIcon from 'assets/icons/new/selection/file-menu-upload.svg';
// types
// styles
import { IMediaItem } from '_interfaces/types/post.types';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { MentionIndicator } from 'components/mention-indicator';
import { useSearchParams } from 'react-router-dom';
import { useGetMeQuery } from 'redux-store/api/auth';
import useToast from 'hooks/useToast';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { IProjectProfiles } from 'redux-store/api/projects';
import { newPostStyles } from './styles/styles';
import { prefilledPostMediaAtom, selectedProjectAtom } from 'atoms';
import { loadProjectMediaAtom, markProjectMediaItemsAsLinkedAtom } from 'atoms/media/projectMedia';
import Tag from '../tag';
import AddTagButton from '../add-tags-button';
import { useAtomValue, useSetAtom } from 'jotai';
import { AudienceContainer } from 'widgets/side-panel/sections/activity-feed/old/components/post/audience/audience-container/audience-container';
import { useAudience } from 'widgets/side-panel/sections/activity-feed/old/components/post/audience/hooks';
import { transformAudience } from 'widgets/side-panel/sections/activity-feed/old/components/post/audience/utils';
import FileCabinetAttachmentPicker from './file-cabinet-attachment-picker';

export type TAction = 'CREATE_POST' | 'UPDATE_POST' | 'CREATE_COMMENT' | 'UPDATE_COMMENT';

export interface IPostProps {
  commentAudience?: {
    postVisibility: PostVisibilityEnum;
    visibility: number[];
  };
  isExpanded?: boolean;
  projectId: number;
  actionType: TAction;
  companyId: string; // Keep as string
  editablePost?: IPost;
  editableComment?: IPostComment;
  uploadPath?: string;
  replyToProfile?: {
    id: number;
    userId: number;
    firstName: string;
    lastName: string;
    avatar?: string | null;
    role?: string;
    email?: string;
  };
  mentionedUser?: IActivityFeedProfile;
  onResetMentionedProfile?: () => void;
  mentionOptions: ICustomMentionData[];
  projectProfiles?: IProjectProfiles[];
  onExpand?: (isExpanded: boolean) => void;
  setOpen?: (open: boolean) => void;
  onScrollToTop?: () => void;
  getCustomInputStyles?: (isError: boolean) => IStyles;
  onUpdatePost?: (body: Partial<IPost>) => Promise<void> | void;
  onCreatePost?: (body: Partial<IPost>) => Promise<void> | void;
  onUpdateComment?: (body: Partial<IPostComment>) => Promise<void> | void;
  onCreateComment?: (body: Partial<IPostComment>) => Promise<void> | void;
  isInModal?: boolean;
  feedContextType?: 'ACTIVITY_FEED' | 'FILE_FEED' | 'SCHEDULE_FEED' | 'SELECTIONS_FEED';
  feedContextItemId?: string;
  parentPostId?: string;
}

const AudioPlayerWrapper: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid sx={{ width: '100%', height: '50px' }}>{children}</Grid>
);

// File upload configuration
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILE_COUNT = 10;

// Convert to comma-separated strings for attr-accept compatibility
const ACCEPTED_FILE_TYPES_STRING = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
  'audio/aac',
  'audio/ogg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

const ACCEPTED_IMAGE_TYPES_STRING = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
].join(',');

interface DropzoneWrapperProps {
  children: React.ReactNode;
  onFilesSelected: (files: File[]) => void;
  accept: string;
  maxFiles: number;
  maxSize: number;
}

const ImageUploadDropzone: FC<DropzoneWrapperProps> = ({
  children,
  onFilesSelected,
  accept,
  maxFiles,
  maxSize,
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFilesSelected,
    accept: accept,
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
};

const Post: FC<IPostProps> = ({
  isExpanded,
  commentAudience,
  projectId,
  actionType,
  companyId,
  editablePost,
  editableComment,
  uploadPath,
  replyToProfile,
  mentionedUser,
  onResetMentionedProfile,
  mentionOptions,
  projectProfiles,
  onExpand,
  getCustomInputStyles,
  setOpen,
  onScrollToTop,
  onUpdatePost,
  onCreatePost,
  onUpdateComment,
  onCreateComment,
  isInModal = false,
  feedContextType = 'ACTIVITY_FEED',
  feedContextItemId,
  parentPostId,
}) => {
  const [registeredMedia, setRegisteredMedia] = useState<Map<string, IMediaItem>>(new Map());
  const registeredMediaRef = useRef(registeredMedia);
  const pendingCommentIdRef = useRef<string | undefined>(undefined);
  const project = useAtomValue(selectedProjectAtom);
  const prefilledMedia = useAtomValue(prefilledPostMediaAtom);
  const setPrefilledMedia = useSetAtom(prefilledPostMediaAtom);
  const markProjectMediaItemsAsLinked = useSetAtom(markProjectMediaItemsAsLinkedAtom);
  const reloadProjectMedia = useSetAtom(loadProjectMediaAtom);
  const showToast = useToast();

  const canConsumePrefilledMedia =
    actionType === 'CREATE_POST' && !editablePost?.id && !editableComment?.id;

  const emojiPlugin = useMemo(
    () =>
      createEmojiPlugin({
        useNativeArt: true,
        selectButtonContent: <EmojiIcon />,
        selectPopperOptions: {
          strategy: 'fixed',
          placement: 'bottom-start',
        },
        suggestionsPopperOptions: {
          strategy: 'fixed',
          placement: 'top',
        },
      }),
    []
  );
  const { EmojiSelect } = emojiPlugin;

  const { data: userMe } = useGetMeQuery();

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const {
    isError: isAudienceError,
    showAudience,
    convertedSpecificUsers,
    convertedLatestAudience,
    setLatestPostAudience,
  } = useAudience({
    isDraft: editablePost?.status === 'DRAFT',
    postVisibility: editablePost?.postVisibility,
    visibility: editablePost?.visibility,
    projectId,
    userPermissions,
    actionType,
    audienceGroups: editablePost?.audienceGroups,
    audienceRoles: editablePost?.audienceRoles,
    visibleProfileIds: editablePost?.visibleProfileIds,
    projectProfiles: projectProfiles || project?.profiles,
  });

  const existingContent = useMemo(
    () =>
      editablePost?.message
        ? editablePost.message
        : editableComment?.message
          ? editableComment.message
          : '',
    [editablePost?.message, editableComment?.message]
  );

  const [editorState, setEditorState] = useState(() => {
    if (actionType === 'CREATE_COMMENT' && replyToProfile && replyToProfile.userId !== userMe?.id) {
      const { mentionEntity, textContent } = createMentionToContentHelper({
        ...replyToProfile,
        role: replyToProfile.role ?? '',
        avatar: replyToProfile.avatar ?? null,
      });
      const draftReplyContent = createDraftContent({
        profiles: project.profiles,
        blocksContent: [textContent],
        mentions: [mentionEntity],
        hashtags: [],
      });
      return EditorState.createWithContent(convertFromRaw(draftReplyContent));
    }

    if (actionType === 'CREATE_POST' && mentionedUser) {
      const { mentionEntity, textContent } = createMentionToContentHelper(mentionedUser);
      const draftMentionContent = createDraftContent({
        profiles: project.profiles,
        blocksContent: [textContent],
        mentions: [mentionEntity],
        hashtags: [],
      });
      return EditorState.createWithContent(convertFromRaw(draftMentionContent));
    }

    if (existingContent) {
      const mentions = editablePost?.mentions || editableComment?.mentions || [];
      const mentionObjects = mentions.map((mention, index) => ({
        profileId: mention.profileId || index + 1,
        length: mention.length,
        offset: mention.offset,
        block: mention.block,
        key: index,
      }));

      const draftContent = createDraftContent({
        profiles: project.profiles,
        blocksContent: convertHtml(existingContent),
        mentions: mentionObjects,
        hashtags: editablePost?.hashtags || editableComment?.hashtags || [],
      });

      return EditorState.createWithContent(convertFromRaw(draftContent));
    }

    return EditorState.createEmpty();
  });

  // Reset mentionedProfile after the mention has been consumed by the editor initializer
  useEffect(() => {
    if (mentionedUser) onResetMentionedProfile?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rawEditorContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
  const sanitizedContent = rawEditorContent === '<p></p>\n' ? '' : rawEditorContent;

  const [postIdState, setPostIdState] = useState<string | null>(() => {
    if (actionType === 'CREATE_POST' && uploadPath) {
      const postRef = collection(db, uploadPath);
      const newPostRef = doc(postRef);
      return newPostRef.id;
    }
    return null;
  });

  const resetPostId = useCallback(() => {
    if (actionType === 'CREATE_POST' && uploadPath) {
      const postRef = collection(db, uploadPath);
      const newPostRef = doc(postRef);
      setPostIdState(newPostRef.id);
    }
  }, [actionType, uploadPath]);

  const postId = postIdState;

  // Update upload path to include the post ID
  const uploadPathWithPostId = useMemo(() => {
    if (postId && actionType === 'CREATE_POST') {
      return `${uploadPath}/${postId}`;
    }
    return uploadPath;
  }, [uploadPath, postId, actionType]);

  const { uploadMultipleFiles, getAllUploads, isUploading, clearAllUploads, clearUpload } =
    useFileUpload({
      uploadPath: uploadPathWithPostId,
      onUploadError: (error) => {
        showToast({
          type: 'error',
          message:
            error instanceof StorageLimitExceededError
              ? error.message
              : error?.message || 'Failed to upload file. Please try again.',
        });
      },
    });

  // State for transcoding thumbnails (must be declared before uploadedFiles)
  const [transcodedThumbnails, setTranscodedThumbnails] = useState<Map<string, string>>(new Map());
  const [uploadTimestamps, setUploadTimestamps] = useState<Map<string, number>>(new Map());
  const [existingMediaThumbnails, setExistingMediaThumbnails] = useState<Map<string, string>>(
    new Map()
  );

  const [existingMedia, setExistingMedia] = useState<IPost['media']>(() => {
    if (editablePost?.media?.length) {
      return editablePost.media;
    }
    if (editableComment?.media?.length) {
      return editableComment.media;
    }
    if (canConsumePrefilledMedia && prefilledMedia.length) {
      return prefilledMedia;
    }
    if (editableComment?.uploads?.length) {
      return editableComment.uploads.map((upload) => {
        const isObject = typeof upload === 'object' && upload !== null;
        const uploadUrl: string = isObject ? (upload as { url: string }).url : String(upload);
        const uploadName: string =
          isObject && (upload as { url: string; name?: string }).name
            ? (upload as { url: string; name?: string }).name!
            : getFilenameFromUrl(uploadUrl);
        return {
          url: uploadUrl,
          name: uploadName,
          type: isImageFilename(uploadName) ? ('image' as const) : ('file' as const),
        };
      });
    }
    return [];
  });

  // Consume prefilled media (e.g. from "Create Post" on the Media tab):
  // merge into existingMedia (de-duplicated by fileCabinetId or url) and clear the atom
  // so re-opening the composer later doesn't re-attach the same items.
  useEffect(() => {
    if (!canConsumePrefilledMedia || !prefilledMedia.length) return;

    setExistingMedia((prev) => {
      const existing = prev || [];
      const seenFcIds = new Set(
        existing.map((m) => m.fileCabinetId).filter((id): id is number => !!id)
      );
      const seenUrls = new Set(existing.map((m) => m.url).filter((u): u is string => !!u));
      const additions = prefilledMedia.filter((m) => {
        if (m.fileCabinetId && seenFcIds.has(m.fileCabinetId)) return false;
        if (m.url && seenUrls.has(m.url)) return false;
        return true;
      });
      if (!additions.length) return existing;
      return [...existing, ...additions];
    });

    setPrefilledMedia([]);
  }, [canConsumePrefilledMedia, prefilledMedia, setPrefilledMedia]);

  // Resolve transcoded thumbnail storage paths to download URLs for existing draft media
  useEffect(() => {
    existingMedia.forEach((media) => {
      const thumbnailPath = media.transcoded?.thumbnail;
      if (
        thumbnailPath &&
        !thumbnailPath.startsWith('http') &&
        !existingMediaThumbnails.has(media.url)
      ) {
        const storageRef = ref(storage, thumbnailPath);
        getDownloadURL(storageRef)
          .then((downloadURL) => {
            setExistingMediaThumbnails((prev) => {
              const updated = new Map(prev);
              updated.set(media.url, downloadURL);
              return updated;
            });
          })
          .catch(() => {});
      }
    });
  }, [existingMedia, existingMediaThumbnails]);

  // Generate thumbnails for existing draft videos that don't have previewUrl or transcoded thumbnail
  const videoEntriesForThumbnails = useMemo(
    () =>
      existingMedia
        .filter(
          (media) =>
            media.type === 'video' &&
            !media.options?.previewUrl &&
            !media.transcoded?.thumbnail &&
            media.url
        )
        .map((media) => ({
          id: media.url as string,
          url: media.url as string,
          fileCabinetId: media.fileCabinetId,
        })),
    [existingMedia]
  );

  const generatedVideoThumbnails = useVideoThumbnails(
    videoEntriesForThumbnails,
    projectId && companyId ? { projectId, companyId } : undefined,
  );

  const uploadedFiles: IUploadingFiles[] = getAllUploads().map((upload) => {
    let status: StatusValue;
    if (upload.status === 'error') {
      status = 'error_file_size';
    } else if (upload.status === 'uploading') {
      status = 'uploading';
    } else if (upload.status === 'completed') {
      status = 'done';
    } else {
      status = 'uploading';
    }

    // Get video thumbnail: prefer transcoded, fall back to client-generated
    const thumbnailUrl = transcodedThumbnails.get(upload.fileId) || upload.thumbnailUrl;

    return {
      id: upload.fileId,
      url: upload.originalUrl || '',
      name: upload.fileName,
      tempId: upload.fileId,
      status,
      percent: upload.progress,
      size: 0, // We don't track size in the new system
      mimeType: getMimeTypeFromFileName(upload.fileName), // Add mime type for proper preview
      previewUrl: thumbnailUrl, // Full download URL for video thumbnails
      description: null,
    };
  });

  // Convert existing media to IUploadingFiles format for display
  const existingMediaAsUploadingFiles: IUploadingFiles[] = useMemo(() => {
    return existingMedia.map((media) => ({
      id: media.url || '',
      url: media.url || '',
      name: media.name || 'file',
      tempId: media.url || '',
      status: 'done' as StatusValue,
      percent: 100,
      size: 0,
      mimeType: getMimeTypeFromFileName(media.name || ''),
      previewUrl:
        existingMediaThumbnails.get(media.url || '') ||
        media.transcoded?.thumbnail ||
        media.options?.previewUrl ||
        generatedVideoThumbnails.get(media.url || ''),
      duration: media.options?.duration,
      description: media.description != null ? media.description : null,
      ...(media.fileCabinetId != null ? { fileCabinetId: media.fileCabinetId } : {}),
    }));
  }, [existingMedia, existingMediaThumbnails, generatedVideoThumbnails]);

  const [isEmojiSelectorOpen, setIsEmojiSelectorOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileMenuAnchorEl, setFileMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isFileCabinetPickerOpen, setIsFileCabinetPickerOpen] = useState(false);
  const submittingRef = useRef(false);
  const [searchParams] = useSearchParams();

  const [committedPhaseValues, setCommittedPhaseValues] = useState<ITag[]>([]);
  const [committedPhaseItemsValues, setCommittedPhaseItemsValues] = useState<ITag[]>([]);
  const [phaseValues, setPhaseValues] = useState<ITag[]>([]);
  const [phaseItemsValues, setPhaseItemsValues] = useState<ITag[]>([]);

  const committedPhaseValuesRef = useRef<ITag[]>([]);
  const committedPhaseItemsValuesRef = useRef<ITag[]>([]);

  useEffect(() => {
    if (editablePost && project?.phases) {
      const phases = (editablePost.phaseIds || [])
        .map((id) => {
          const phase = project.phases?.find((p) => p.id === id);
          return phase ? { id: phase.id, title: phase.title, color: phase.color } : null;
        })
        .filter(Boolean) as ITag[];
      committedPhaseValuesRef.current = phases;
      setCommittedPhaseValues(phases);
      setPhaseValues(phases);

      const phaseItems = (editablePost.phaseItemIds || [])
        .map((id) => {
          for (const phase of project.phases || []) {
            const item = phase.items?.find((i) => i.id === id);
            if (item) return { id: item.id, title: item.title, color: phase.color };
          }
          return null;
        })
        .filter(Boolean) as ITag[];
      committedPhaseItemsValuesRef.current = phaseItems;
      setCommittedPhaseItemsValues(phaseItems);
      setPhaseItemsValues(phaseItems);
    }
  }, [editablePost, project?.phases]);

  const handleSubmitTags = useCallback((phases: ITag[], phaseItems: ITag[]) => {
    committedPhaseValuesRef.current = phases;
    committedPhaseItemsValuesRef.current = phaseItems;
    setCommittedPhaseValues(phases);
    setCommittedPhaseItemsValues(phaseItems);
    setPhaseValues(phases);
    setPhaseItemsValues(phaseItems);
  }, []);

  const handleSetDefaultTags = useCallback(() => {
    setPhaseValues(committedPhaseValuesRef.current);
    setPhaseItemsValues(committedPhaseItemsValuesRef.current);
  }, []);

  const selectedTags = useMemo(() => {
    return [...committedPhaseValues, ...committedPhaseItemsValues];
  }, [committedPhaseValues, committedPhaseItemsValues]);

  useEffect(() => {
    if (!companyId || !projectId) return;

    const q = query(
      collection(db, 'transcodingJobs'),
      where('companyId', '==', companyId),
      where('projectId', '==', projectId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const uploadedFiles = getAllUploads();

      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const job = change.doc.data() as TranscodingJob;

          const matchingFile = uploadedFiles.find(
            (upload) =>
              (job.originalPath && upload.storagePath === job.originalPath) ||
              upload.fileName === job.originalFileName,
          );

          const uploadTimestamp = uploadTimestamps.get(matchingFile?.fileId || '');
          const jobCreatedAt = job.createdAt?.toMillis?.() || job.createdAt?.seconds * 1000 || 0;
          const uploadStartedAfter = uploadTimestamp ? jobCreatedAt >= uploadTimestamp : false;

          const shouldProcess =
            matchingFile &&
            job.status === 'completed' &&
            job.transcodedFiles &&
            (uploadTimestamp === undefined || uploadStartedAfter);

          if (shouldProcess) {
            const thumbnail = job.transcodedFiles.find((file) => file.quality === 'thumbnail');

            if (thumbnail) {
              const storageRef = ref(storage, thumbnail.path);
              getDownloadURL(storageRef)
                .then((downloadURL) => {
                  // Store the download URL
                  setTranscodedThumbnails((prev) => {
                    const updated = new Map(prev);
                    updated.set(matchingFile.fileId, downloadURL);
                    return updated;
                  });
                })
                .catch(() => {});
            }
          }
        }
      });
    });

    return () => unsubscribe();
  }, [companyId, projectId, getAllUploads, uploadTimestamps]);

  // Track upload timestamps for each new upload
  useEffect(() => {
    const uploads = getAllUploads();
    uploads.forEach((upload) => {
      if (!uploadTimestamps.has(upload.fileId)) {
        // New upload detected, record timestamp
        setUploadTimestamps((prev) => {
          const updated = new Map(prev);
          updated.set(upload.fileId, Date.now());
          return updated;
        });
      }
    });
  }, [getAllUploads, uploadTimestamps]);

  const form = useForm({
    resolver: yupResolver(
      actionType === 'CREATE_POST' || actionType === 'UPDATE_POST'
        ? activityFeedSchema
        : postReplySchema
    ),
    defaultValues: {
      content: '',
    },
  });

  const hasPendingUploads =
    isUploading || uploadedFiles.some((file) => file.status === 'uploading');

  // --- Auto-draft saving ---
  const draftUploads = useMemo(() => {
    const completedUploads = getAllUploads().filter(
      (upload) => upload.status === 'completed' && upload.originalUrl
    );
    const cabinetUploads = existingMedia.map((media) => ({
      id: String(media.fileCabinetId || media.url || media.name),
      description: media.description || null,
      url: media.url || '',
      name: media.name,
      fileCabinetId: media.fileCabinetId || Array.from(registeredMedia.values()).find((item) => item.url === media.url)?.fileCabinetId,
      mimeType: media.mimeType || getMimeTypeFromFileName(media.name),
    }));
    return [
      ...cabinetUploads,
      ...completedUploads.map((upload) => ({
        id: uploadPathWithPostId ? `${uploadPathWithPostId}/${upload.fileId}` : upload.fileId,
        description: null,
        url: upload.originalUrl || '',
        name: upload.fileName,
        fileCabinetId: Array.from(registeredMedia.values()).find((item) => item.url === upload.originalUrl)?.fileCabinetId,
      })),
    ];
  }, [existingMedia, getAllUploads, uploadPathWithPostId, registeredMedia]);

  const draftData = useMemo(
    () => ({
      uploads: draftUploads,
      projectId,
      audience: transformAudience(convertedLatestAudience, showAudience),
      phaseIds: committedPhaseValues.map((p) => p.id),
      phaseItemIds: committedPhaseItemsValues.map((p) => p.id),
    }),
    [
      draftUploads,
      projectId,
      convertedLatestAudience,
      showAudience,
      committedPhaseValues,
      committedPhaseItemsValues,
    ]
  );

  const shouldSkipDraftSave =
    !isExpanded || actionType === 'UPDATE_POST' || actionType === 'UPDATE_COMMENT';

  const { onPublish: draftOnPublish, isDraftSaving } = useDrafts({
    data: draftData,
    content: sanitizedContent,
    editorState,
    form,
    type: getDraftType(actionType),
    shouldSkip: shouldSkipDraftSave,
    companyId,
    user: {
      userId: userMe?.id?.toString() || '',
      displayName: `${userMe?.firstName || ''} ${userMe?.lastName || ''}`.trim(),
      avatarUrl: userMe?.avatar || '',
      role: userMe?.role || '',
    },
    existingFirestoreDraftId: editablePost?.id,
  });

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      try {
        const { convertHeicFiles } = await import('utils/convertHeicToJpeg');
        const convertedFiles = await convertHeicFiles(files);
        await uploadMultipleFiles(convertedFiles);
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error instanceof StorageLimitExceededError
              ? error.message
              : 'Failed to upload file. Please try again.',
        });
      }
    },
    [uploadMultipleFiles, showToast]
  );

  const { getInputProps: getFileUploadInputProps, open: openFileUploadDialog } = useDropzone({
    onDrop: handleFileUpload,
    accept: ACCEPTED_FILE_TYPES_STRING,
    maxFiles: MAX_FILE_COUNT,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const attachedFileCabinetIds = useMemo(
    () => existingMedia.map((media) => media.fileCabinetId).filter((id): id is number => !!id),
    [existingMedia]
  );

  const handleAddCabinetMedia = useCallback((mediaItems: IMediaItem[]) => {
    setExistingMedia((prev) => {
      const existingIds = new Set(
        prev.map((media) => media.fileCabinetId).filter((id): id is number => !!id)
      );
      const uniqueItems = mediaItems.filter(
        (media) => media.fileCabinetId && !existingIds.has(media.fileCabinetId)
      );
      return [...prev, ...uniqueItems];
    });
  }, []);

  const handleDeleteUploadedData = useCallback(
    (fileId: string) => {
      clearUpload(fileId);
      // Clear the thumbnail and timestamp if they exist
      setTranscodedThumbnails((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });
      setUploadTimestamps((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });
    },
    [clearUpload]
  );

  const handleDeleteExistingMedia = useCallback((mediaUrl: string) => {
    setExistingMedia((prev) => prev.filter((media) => media.url !== mediaUrl));
  }, []);

  const allUploadedFiles = useMemo(() => {
    return [...existingMediaAsUploadingFiles, ...uploadedFiles];
  }, [existingMediaAsUploadingFiles, uploadedFiles]);

  const audioFiles = useMemo(
    () => allUploadedFiles.filter((file) => isAudio(getMimeTypeFromFileName(file.name))),
    [allUploadedFiles]
  );

  const nonAudioFiles = useMemo(
    () => allUploadedFiles.filter((file) => !isAudio(getMimeTypeFromFileName(file.name))),
    [allUploadedFiles]
  );

  const handleDeleteAnyUploadedData = useCallback(
    (fileId: string) => {
      const isExistingMedia = existingMediaAsUploadingFiles.some((media) => media.id === fileId);
      if (isExistingMedia) {
        handleDeleteExistingMedia(fileId);
      } else {
        handleDeleteUploadedData(fileId);
      }
    },
    [existingMediaAsUploadingFiles, handleDeleteExistingMedia, handleDeleteUploadedData]
  );

  const handleBlurInput = useCallback(() => {
    // Handle blur logic if needed
  }, []);

  const onOpenEmojiSelect = useCallback(() => {
    setIsEmojiSelectorOpen(true);
  }, []);

  const onCloseEmojiSelect = useCallback(() => {
    setIsEmojiSelectorOpen(false);
  }, []);

  const handleCollapseInput = useCallback(() => {
    onExpand?.(false);
  }, [onExpand]);

  const enrichMediaWithFileCabinetIds = async (
    items: IMediaItem[],
    contextType: FileLinkContextType,
    contextId: string,
    contextItemId?: string
  ): Promise<IMediaItem[]> => {
    const results = await Promise.allSettled(
      items.map(async (item) => {
        if (!item.url && !item.fileCabinetId) return item;
        const key = JSON.stringify([
          companyId,
          projectId,
          contextType,
          contextId,
          contextItemId,
          item.url || item.fileCabinetId,
        ]);
        const cached = registeredMediaRef.current.get(key);
        if (cached) return { ...item, fileCabinetId: cached.fileCabinetId };
        let enriched = item;
        if (item.fileCabinetId) {
          await createFileLink({
            projectId,
            fileId: item.fileCabinetId,
            contextType,
            contextId,
            contextItemId,
          });
        } else if (item.url) {
          const matchedUpload = getAllUploads().find(
            (upload) => upload.originalUrl === item.url
          );
          const entry = await createMediaEntry({
            projectId,
            name: item.name,
            firebaseUrl: item.url,
            mimeType: item.mimeType || getMimeTypeFromFileName(item.name),
            size:
              typeof matchedUpload?.size === 'number'
                ? String(matchedUpload.size)
                : undefined,
            contextType,
            contextId,
            contextItemId,
            description: item.description || undefined,
            timestamp: item.timestamp || undefined,
            location: item.location || undefined,
            thumbnailUrl: item.options?.previewUrl,
          });
          enriched = { ...item, fileCabinetId: entry.id };
        }
        const next = new Map(registeredMediaRef.current);
        next.set(key, enriched);
        registeredMediaRef.current = next;
        setRegisteredMedia(next);
        return enriched;
      })
    );
    const failure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (failure) throw failure.reason;
    return results.map(
      (result) => (result as PromiseFulfilledResult<IMediaItem>).value
    );
  };

  const commentContextItemId = (commentId?: string): string | undefined =>
    commentId;

  const postContextItemId = (): string | undefined => {
    if (feedContextType === 'ACTIVITY_FEED') return '__NULL__';
    return feedContextItemId;
  };

  // Map feedContextType to Firestore collection name for deriving comment parent path
  const FEED_COLLECTION: Record<
    NonNullable<IPostProps['feedContextType']>,
    string
  > = {
    ACTIVITY_FEED: 'activityFeed',
    FILE_FEED: 'fileFeed',
    SCHEDULE_FEED: 'scheduleFeed',
    SELECTIONS_FEED: 'selectionsFeed',
  };

  const commentParentPath = parentPostId
    ? `companies/${companyId}/projects/${projectId}/${FEED_COLLECTION[feedContextType]}/${parentPostId}`
    : undefined;

  const onSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const rawDraftContent = convertToRaw(editorState.getCurrentContent());
      const mentions = createMentionsArray(rawDraftContent) || [];
      const hashtags = getHashtagsArray(rawDraftContent) || [];
      const content = draftToHtml(rawDraftContent);
      const newMedia: IMediaItem[] = uploadedFiles.map((file) => ({
        type: getMediaType(file.name),
        url: file.url,
        name: file.name,
        mimeType: file.mimeType,
        ...(file.previewUrl
          ? { options: { previewUrl: file.previewUrl } }
          : {}),
      }));
      let removedMedia: IMediaItem[] = [];
      let cleanupContextId: string | undefined;
      let cleanupContextItemId: string | undefined;

      if (actionType === 'CREATE_COMMENT') {
        if (!parentPostId || !commentParentPath || !onCreateComment)
          throw new Error('Unable to prepare comment. Please try again.');
        const newCommentId =
          pendingCommentIdRef.current ||
          doc(collection(db, `${commentParentPath}/comments`)).id;
        pendingCommentIdRef.current = newCommentId;
        const media = await enrichMediaWithFileCabinetIds(
          [...existingMedia, ...newMedia],
          feedContextType,
          parentPostId,
          commentContextItemId(newCommentId)
        );
        await onCreateComment({
          id: newCommentId,
          message: content,
          mentions,
          hashtags,
          uploads: media
            .map((item) => item.url)
            .filter((url): url is string => !!url),
          media,
          postId: parentPostId,
        });
      } else if (actionType === 'UPDATE_COMMENT') {
        if (!editableComment?.id || !onUpdateComment)
          throw new Error('Unable to prepare comment. Please try again.');
        const originalMedia = editableComment.media || [];
        const parentPathParts = (editableComment.parentPath || '').split('/');
        const contextId =
          editableComment.postId || parentPathParts[parentPathParts.length - 1];
        if (!contextId)
          throw new Error('Unable to prepare comment. Please try again.');
        const keptMedia = existingMedia.map((item) => {
          const original = originalMedia.find(
            (media) => media.url === item.url
          );
          return original?.fileCabinetId
            ? { ...item, fileCabinetId: original.fileCabinetId }
            : item;
        });
        const media = await enrichMediaWithFileCabinetIds(
          [...keptMedia, ...newMedia],
          feedContextType,
          contextId,
          commentContextItemId(editableComment.id)
        );
        const keptUrls = new Set(existingMedia.map((item) => item.url));
        removedMedia = originalMedia.filter(
          (item) => item.fileCabinetId && item.url && !keptUrls.has(item.url)
        );
        cleanupContextId = contextId;
        cleanupContextItemId = commentContextItemId(editableComment.id);
        await onUpdateComment({
          ...editableComment,
          message: content,
          mentions,
          hashtags,
          uploads: media
            .map((item) => item.url)
            .filter((url): url is string => !!url),
          media,
        });
      } else if (actionType === 'UPDATE_POST') {
        if (!editablePost?.id || !onUpdatePost)
          throw new Error('Unable to prepare post. Please try again.');
        const media = await enrichMediaWithFileCabinetIds(
          [...existingMedia, ...newMedia],
          feedContextType,
          editablePost.id,
          feedContextItemId
        );
        const keptUrls = new Set(existingMedia.map((item) => item.url));
        const keptIds = new Set(
          existingMedia.map((item) => item.fileCabinetId)
        );
        removedMedia = (editablePost.media || []).filter((item) =>
          item.fileCabinetId
            ? !keptIds.has(item.fileCabinetId)
            : item.url && !keptUrls.has(item.url)
        );
        cleanupContextId = editablePost.id;
        cleanupContextItemId = postContextItemId();
        await onUpdatePost({
          ...editablePost,
          message: content,
          mentions,
          hashtags,
          media,
          phaseIds: committedPhaseValues.map((phase) => phase.id),
          phaseItemIds: committedPhaseItemsValues.map((phase) => phase.id),
          ...transformAudience(convertedLatestAudience, showAudience),
        });
      } else {
        const hasPostBody = !!content && content !== '<p></p>';
        if (!hasPostBody && existingMedia.length === 0 && newMedia.length === 0)
          return;
        if (!onCreatePost)
          throw new Error('Unable to prepare post. Please try again.');
        let effectivePostId = editablePost?.id || postId;
        if (!effectivePostId) {
          const fallbackPath =
            uploadPath ||
            `companies/${companyId}/projects/${projectId}/${FEED_COLLECTION[feedContextType]}`;
          effectivePostId = doc(collection(db, fallbackPath)).id;
          setPostIdState(effectivePostId);
        }
        const media = await enrichMediaWithFileCabinetIds(
          [...existingMedia, ...newMedia],
          feedContextType,
          effectivePostId,
          feedContextItemId
        );
        await onCreatePost({
          id: effectivePostId,
          message: hasPostBody ? content : '',
          mentions,
          hashtags,
          media,
          phaseIds: committedPhaseValues.map((phase) => phase.id),
          phaseItemIds: committedPhaseItemsValues.map((phase) => phase.id),
          ...transformAudience(convertedLatestAudience, showAudience),
        });
        const linkedFcIds = existingMedia
          .map((item) => item.fileCabinetId)
          .filter((id): id is number => !!id);
        if (linkedFcIds.length > 0) {
          markProjectMediaItemsAsLinked(linkedFcIds);
          void reloadProjectMedia(true);
        }
        setPrefilledMedia([]);
        resetPostId();
        committedPhaseValuesRef.current = [];
        committedPhaseItemsValuesRef.current = [];
        setCommittedPhaseValues([]);
        setCommittedPhaseItemsValues([]);
        setPhaseValues([]);
        setPhaseItemsValues([]);
        onExpand?.(false);
      }

      if (cleanupContextId && removedMedia.length > 0) {
        const contextId = cleanupContextId;
        void Promise.allSettled(
          removedMedia
            .filter((item) => !!item.fileCabinetId)
            .map((item) =>
              deleteMediaEntry(
                projectId,
                item.fileCabinetId!,
                contextId,
                cleanupContextItemId
              )
            )
        );
      }
      draftOnPublish();
      clearAllUploads();
      setExistingMedia([]);
      registeredMediaRef.current = new Map();
      setRegisteredMedia(new Map());
      pendingCommentIdRef.current = undefined;
      setOpen?.(false);
      setEditorState(EditorState.createEmpty());
    } catch (error) {
      const failure = error as {
        response?: { data?: { message?: string } };
        data?: { message?: string };
        message?: string;
      };
      showToast({
        type: 'error',
        message:
          failure?.response?.data?.message ||
          failure?.data?.message ||
          failure?.message ||
          'Failed to save. Please try again.',
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isError = useMemo(() => !!form.formState.errors?.content, [form.formState.errors?.content]);

  const inputStyles = useMemo(
    () => ({
      '.mnw6qvm': {
        borderRadius: '12px',
        marginTop: '12px',
        filter:
          'drop-shadow(0px 4px 10px rgba(35, 37, 47, 0.1)) drop-shadow(1px 2px 2px rgba(35, 37, 47, 0.04))',
        boxShadow: 'none',
      },
      '.DraftEditor-root': {
        height: '195px',
        minHeight: 'unset',
      },
      '.DraftEditor-editorContainer': {
        height: '195px',
        minHeight: 'unset',
        overflowY: 'auto',
        borderRadius: '12px',
        outline: isError ? '1px solid #d32f2f' : '1px solid #EDEFF7',
        fontFamily:
          "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
        div: { boxSizing: 'border-box' },
        '&:hover': {
          outline: '1px solid #EDEFF7',
        },
        '&:focus-within': {
          outline: '1px solid #EDEFF7',
        },
      },
    }),
    [isError]
  );

  const postDataForUpdate: Partial<IPost> = editablePost
    ? {
        message: editablePost.message,
        media: editablePost.media,
      }
    : null;

  const mockEditablePostBody = useMemo(() => {
    if (!editablePost) return undefined;

    return {
      id: parseInt(editablePost.id || '0') || 0,
      projectId: parseInt(editablePost.projectId || '0') || 0,
      content: editablePost.message || '',
      fileId: editablePost.itemId || '',
      fileName: editablePost.itemName || '',
      uploads:
        editablePost.media?.map((media) => ({
          id: media.url,
          description: media.description || null,
          location: null,
          timestamp: null,
        })) || [],
      phaseIds: [],
      phaseItemIds: [],
      audienceGroups: [],
      audienceRoles: [],
      postVisibility: editablePost?.postVisibility || 'EVERYONE',
      mentions: [],
      visibleProfileIds: [],
      status: ActivityFeedStatusEnum.PUBLISHED,
      wasPreviouslyDraft: false,
    };
  }, [editablePost]);

  const isComposerUploadedMediaCaptionEditable =
    (editablePost?.createdBy?.userId === userMe?.userId?.toString() ||
      (!!userPermissions?.ACTIVITY_FEED_ALL_POSTS?.canUpdate &&
        !!userPermissions?.ENABLE_ACTIVITY_FEED?.canUpdate)) &&
    actionType !== 'CREATE_POST' &&
    actionType !== 'CREATE_COMMENT';

  useEffect(() => {
    const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    form.setValue('content', content, {
      shouldDirty: true,
    });
  }, [editorState, form.setValue]);

  useDeleteUnsupportedEmojies(isEmojiSelectorOpen);

  const initials = `${userMe?.firstName?.[0] || 'U'}${userMe?.lastName?.[0] || 'S'}`;

  const isPost = ['CREATE_POST', 'UPDATE_POST'].includes(actionType);

  const mentionOptionsByAudience = useMemo(() => {
    const audience = commentAudience?.postVisibility
      ? commentAudience
      : transformAudience(convertedLatestAudience, showAudience);

    if (!audience) return [];

    const { postVisibility, visibility } = audience;

    if (postVisibility === PostVisibilityEnum.EVERYONE) {
      return mentionOptions;
    }

    if (postVisibility === PostVisibilityEnum.JUST_ME) {
      return [];
    }

    if (!visibility?.length) {
      return [];
    }

    return mentionOptions.filter((user) => visibility.includes(Number(user.id)));
  }, [convertedLatestAudience, showAudience, commentAudience, mentionOptions]);

  return (
    <>
      {isExpanded && (
        <FormProvider {...form}>
          <Grid sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Grid
              component="form"
              onSubmit={form.handleSubmit(onSubmit)}
              sx={{
                display: 'flex',
                width: '100%',
              }}
            >
              <Collapse sx={{ display: 'flex', width: '100%' }} in={true}>
                <Grid sx={newPostStyles.expandedInputContainer}>
                  {actionType === 'CREATE_POST' ? (
                    <Grid sx={newPostStyles.minimizeBtnContainer}>
                      <Button sx={newPostStyles.minimizeBtn} onClick={handleCollapseInput}>
                        <ChevronIcon
                          style={{
                            height: '21px',
                            width: '24px',
                          }}
                        />
                      </Button>
                    </Grid>
                  ) : null}
                  <Grid
                    sx={{
                      ...newPostStyles.expandedInput,
                      padding: actionType === 'CREATE_POST' ? '16px' : '0',
                      backgroundColor: actionType === 'CREATE_POST' ? '#FAFBFC' : '#FFFFFF',
                    }}
                  >
                    {showAudience && !isInModal && (
                      <AudienceContainer
                        projectId={project?.id}
                        projectProfiles={project?.profiles}
                        projectAudiences={convertedSpecificUsers}
                        postAudience={convertedLatestAudience}
                        isLoading={!convertedLatestAudience}
                        isError={isAudienceError}
                        onSetLatestPostAudience={setLatestPostAudience}
                      />
                    )}
                    <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                      <Grid sx={newPostStyles.textEditorContainer}>
                        {isPost && (
                          <MentionIndicator
                            hide={!!sanitizedContent}
                            sx={newPostStyles.mentionIndicator}
                          />
                        )}
                        <TextEditor
                          projectId={projectId}
                          autofocus={true}
                          placeholder={
                            actionType === 'CREATE_COMMENT' || actionType === 'UPDATE_COMMENT'
                              ? 'Leave a comment…'
                              : 'Write your message…'
                          }
                          editorState={editorState}
                          isError={isError}
                          inputStyles={
                            getCustomInputStyles ? getCustomInputStyles(isError) : inputStyles
                          }
                          onChange={setEditorState}
                          onBlur={handleBlurInput}
                          onSubmit={form.handleSubmit(onSubmit)}
                          setUploadedFiles={() => {}}
                          mentionsList={mentionOptionsByAudience}
                          saveHeight={
                            actionType === 'CREATE_COMMENT' &&
                            !replyToProfile &&
                            searchParams.has('postId')
                          }
                          emojiPlugin={emojiPlugin}
                        />
                      </Grid>
                      {actionType === 'CREATE_POST' || actionType === 'UPDATE_POST' ? (
                        <Grid sx={newPostStyles.tagsContainer}>
                          {selectedTags?.length
                            ? selectedTags
                                .slice(0, 2)
                                .map((tag) => <Tag key={tag.id} option={tag} bgColor="#FFF" />)
                            : null}
                          {selectedTags?.length && selectedTags?.length > 2 ? (
                            <Tag
                              key={0}
                              bgColor="#FFF"
                              option={{ id: 0, title: `+${selectedTags.length - 2} more` }}
                            />
                          ) : null}
                          <AddTagButton
                            phases={project?.phases}
                            phaseValues={phaseValues}
                            phaseItemsValues={phaseItemsValues}
                            setPhaseValues={setPhaseValues}
                            setPhaseItemsValues={setPhaseItemsValues}
                            handleSubmitTags={handleSubmitTags}
                            handleSetDefaultTags={handleSetDefaultTags}
                          />
                        </Grid>
                      ) : null}
                      {nonAudioFiles.length > 0 || audioFiles.length > 0 ? (
                        <Grid sx={{ width: '100%' }}>
                          <UploadedImages
                            type="new_images"
                            containerStyles={{
                              width: '100%',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '12px',
                              flexDirection: 'row',
                              marginTop: '12px',
                            }}
                            uploadingFiles={nonAudioFiles}
                            handleDeleteUploadedData={handleDeleteAnyUploadedData}
                            isDescriptionEdtable={isComposerUploadedMediaCaptionEditable}
                          />
                          <UploadedFiles
                            type="new_files"
                            uploadingFiles={nonAudioFiles}
                            handleDeleteUploadedData={handleDeleteAnyUploadedData}
                          />
                          {audioFiles.map((audio) => (
                            <Grid
                              key={audio.tempId}
                              sx={{ position: 'relative', width: '100%', mt: '12px' }}
                            >
                              <Grid
                                sx={{
                                  width: '100%',
                                  backgroundColor: '#f3f4f9',
                                  borderRadius: '12px',
                                  padding: '8px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    lineHeight: '146%',
                                    fontWeight: '400',
                                  }}
                                >
                                  Audio Message
                                </Typography>
                                <Grid sx={{ width: '100%', height: '50px' }}>
                                  <Player controls url={audio.url} wrapper={AudioPlayerWrapper} />
                                </Grid>
                              </Grid>
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  padding: 0,
                                  '& svg': { width: '20px', height: '20px' },
                                }}
                                onClick={() => handleDeleteAnyUploadedData(audio.id)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Grid>
                          ))}
                        </Grid>
                      ) : null}
                    </Grid>
                    <Grid sx={newPostStyles.actionsContainer}>
                      <Grid sx={newPostStyles.actionBtnsContainer}>
                        <input {...getFileUploadInputProps()} />
                        <IconButton
                          sx={newPostStyles.actionIconBtn}
                          onClick={(event) => setFileMenuAnchorEl(event.currentTarget)}
                        >
                          <AttachFileIcon />
                        </IconButton>
                        <Popover
                          anchorEl={fileMenuAnchorEl}
                          open={!!fileMenuAnchorEl}
                          onClose={() => setFileMenuAnchorEl(null)}
                          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          PaperProps={{
                            sx: newPostStyles.fileMenuPaper,
                          }}
                        >
                          <Grid
                            component="button"
                            type="button"
                            onClick={() => {
                              setFileMenuAnchorEl(null);
                              openFileUploadDialog();
                            }}
                            sx={newPostStyles.fileMenuItem}
                          >
                            <Grid sx={newPostStyles.fileMenuItemIcon}>
                              <UploadFileIcon />
                            </Grid>
                            Upload File
                          </Grid>
                          <Grid
                            component="button"
                            type="button"
                            disabled={!userPermissions?.VIEW_PROJECT_FOLDERS?.canRead}
                            onClick={() => {
                              setFileMenuAnchorEl(null);
                              setIsFileCabinetPickerOpen(true);
                            }}
                            sx={newPostStyles.fileMenuItem}
                          >
                            <Grid sx={newPostStyles.fileMenuItemIcon}>
                              <CabinetFileIcon />
                            </Grid>
                            Select from Cabinet
                          </Grid>
                        </Popover>

                        <ImageUploadDropzone
                          onFilesSelected={handleFileUpload}
                          accept={ACCEPTED_IMAGE_TYPES_STRING}
                          maxFiles={MAX_FILE_COUNT}
                          maxSize={MAX_FILE_SIZE}
                        >
                          <IconButton component="label">
                            <AttachImageIcon />
                          </IconButton>
                        </ImageUploadDropzone>
                        <EmojiSelect onClose={onOpenEmojiSelect} onOpen={onCloseEmojiSelect} />
                      </Grid>
                      {actionType === 'CREATE_POST' ? (
                        <Button
                          disabled={
                            isSubmitting ||
                            hasPendingUploads ||
                            isDraftSaving ||
                            (!editorState.getCurrentContent().hasText() &&
                              allUploadedFiles.length === 0)
                          }
                          type="submit"
                          sx={newPostStyles.submitBtn}
                        >
                          {isSubmitting ? 'Publishing...' : isDraftSaving ? 'Saving...' : 'Publish'}
                        </Button>
                      ) : (
                        <Grid
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            gap: '12px',
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setOpen?.(false);
                            }}
                            sx={newPostStyles.cancelBtn}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={
                              isSubmitting ||
                              hasPendingUploads ||
                              isDraftSaving ||
                              (!editorState.getCurrentContent().hasText() &&
                                allUploadedFiles.length === 0)
                            }
                            type="submit"
                            sx={newPostStyles.submitBtn}
                          >
                            {actionType === 'CREATE_COMMENT'
                              ? isSubmitting
                                ? 'Posting...'
                                : isDraftSaving
                                  ? 'Saving...'
                                  : 'Post'
                              : isSubmitting
                                ? 'Saving...'
                                : 'Save'}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </FormProvider>
      )}

      {!isExpanded && (
        <Grid
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            backgroundColor: '#F4F5F9',
            gap: '12px',
            padding: '12px 20px',
          }}
        >
          {userMe?.avatar ? (
            <Avatar
              alt="avatar"
              sx={{
                width: '36px',
                height: '36px',
                borderRadius: '7.2px',
              }}
              src={getImageBySize(userMe?.avatar, 36, 36)}
              variant="square"
            />
          ) : (
            <DefaultAvatar
              initials={initials}
              variant="square"
              sx={{
                width: '36px',
                height: '36px',
                borderRadius: '7.2px',
              }}
            />
          )}
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              cursor: 'pointer',
              width: '100%',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
            }}
            onClick={() => onExpand?.(true)}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                color: '#8C8F9D',
                fontSize: '14px',
                fontStyle: 'normal',
                lineHeight: '140%',
                fontWeight: '400',
                '-webkit-user-select': 'none',
                '-ms-user-select': 'none',
                'user-select': 'none',
              }}
            >
              Start a new discussion...
            </Typography>
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                '& svg': {
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              {uploadPath && <AttachFileIcon />}
              {uploadPath && <AttachImageIcon />}
              <EmojiIcon />
            </Grid>
          </Grid>
        </Grid>
      )}

      <FileCabinetAttachmentPicker
        open={isFileCabinetPickerOpen}
        projectId={projectId}
        attachedFileCabinetIds={attachedFileCabinetIds}
        onClose={() => setIsFileCabinetPickerOpen(false)}
        onAddFiles={handleAddCabinetMedia}
      />
    </>
  );
};

export default Post;
